import React, { useContext, useMemo } from "react";
import Select from "common/Select/Select";
import { FormFieldTitle } from "features/bots/common/styles";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import StyledRadioGroup from "common/Radio/StyledRadioGroup";
import useMedia from "utils/hooks/useMedia";
import { SMALL_SCREEN } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import TextWithIcon from "common/Text/TextWithIcon";
import { useExchangesMetadata } from "@shared/utils/hooks/misc";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { EntryGraceBaseline } from "@shared/features/bots/BotForm/common/types";
import {
  ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT,
  GRACE_TARGET_EXCHANGE_FEATURE_FLAG,
} from "@shared/features/bots/BotForm/common/constants";
import { NumberOption } from "@shared/types";

interface GracePercentProps {
  isEntry: boolean;
}

const GracePercent = ({ isEntry }: GracePercentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { exchanges: allExchanges, exchangeToDisplayName } =
    useExchangesMetadata();
  const singleColumnLayout = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const twoColumnsLayout = useMedia(["(max-width: 1300px)"], [true], false);

  const {
    botGeneralData,
    entryGracePercent,
    entryGraceBaseline,
    setEntryGracePercent,
    setEntryGraceBaseline,
    entryGraceTargetExchange,
    setEntryGraceTargetExchange,
    takeProfitGracePercent,
    setTakeProfitGracePercent,
    takeProfitGraceTargetExchange,
    setTakeProfitGraceTargetExchange,
    account,
    subGroup,
  } = useContext(BotFormContext)!;

  const allowGraceTargetExchange = subGroup?.feature_flags?.includes(
    GRACE_TARGET_EXCHANGE_FEATURE_FLAG
  );

  const gracePercentTitle = isEntry
    ? t("labels.maximum_price_cap_percent")
    : allowGraceTargetExchange
    ? t("labels.grace_percent")
    : undefined;

  const gracePercentValue = isEntry
    ? isSymbol(entryGracePercent)
      ? undefined
      : entryGracePercent
    : isSymbol(takeProfitGracePercent)
    ? undefined
    : takeProfitGracePercent;

  const gracePercentOnChange = isEntry
    ? setEntryGracePercent
    : setTakeProfitGracePercent;

  const gracePercentPlaceholder = isEntry
    ? isSymbol(entryGracePercent)
      ? t("labels.multi")
      : undefined
    : isSymbol(takeProfitGracePercent)
    ? t("labels.multi")
    : undefined;

  const rawOptions = isEntry
    ? botGeneralData?.options["Entry Grace Percentage"]?.[0]
    : botGeneralData?.options["Take Profit Grace Percentage"]?.[0];

  const gracePercentOptions = rawOptions
    ?.filter((option: NumberOption) => !!option.value)
    .map((option: NumberOption) => ({
      value: option.value,
      name: `${option.name}%`,
    }));

  const selectedBaseline = isEntry
    ? entryGraceBaseline ?? EntryGraceBaseline.PRICE_REACHED
    : undefined;

  const graceBaselineTitle = isEntry ? t("labels.when_to_activate") : undefined;
  const graceBaselineValue = selectedBaseline;
  const graceBaselineOptions = isEntry
    ? [EntryGraceBaseline.PRICE_REACHED, EntryGraceBaseline.IMMEDIATE].map(
        (baseline) => ({
          value: baseline,
          title: t(ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT[baseline]),
          selected: selectedBaseline === baseline,
          onClick: () => setEntryGraceBaseline(baseline),
        })
      )
    : undefined;
  const graceBaselineOnChange = isEntry ? setEntryGraceBaseline : undefined;

  const graceTargetExchangeValue = isEntry
    ? isSymbol(entryGraceTargetExchange)
      ? undefined
      : entryGraceTargetExchange ?? ACCOUNT_EXCHANGE_VALUE
    : isSymbol(takeProfitGraceTargetExchange)
    ? undefined
    : takeProfitGraceTargetExchange ?? ACCOUNT_EXCHANGE_VALUE;

  const graceTargetExchangePlaceholder = isEntry
    ? isSymbol(entryGraceTargetExchange)
      ? t("labels.multi")
      : undefined
    : isSymbol(takeProfitGraceTargetExchange)
    ? t("labels.multi")
    : undefined;

  const graceTargetExchangeOnChange = (exchange?: string) => {
    const finalValue =
      exchange === ACCOUNT_EXCHANGE_VALUE ? undefined : exchange;
    if (isEntry) {
      setEntryGraceTargetExchange(finalValue);
    } else {
      setTakeProfitGraceTargetExchange(finalValue);
    }
  };

  const exchangeOptions = useMemo(() => {
    const options = (allExchanges ?? []).map((exchange: string) => {
      const displayName = exchangeToDisplayName?.[exchange] ?? exchange;

      return {
        value: exchange,
        label: (
          <TextWithIcon
            coinOrExchangeName={exchange}
            text={displayName}
            iconType={ICON_TYPES.EXCHANGE}
          />
        ),
      };
    });

    const currentAccountExchange = !isSymbol(account)
      ? account?.exchange
      : undefined;

    return [
      {
        value: ACCOUNT_EXCHANGE_VALUE,
        label: (
          <TextWithIcon
            coinOrExchangeName={currentAccountExchange}
            text={t("labels.current_account")}
            iconType={ICON_TYPES.EXCHANGE}
          />
        ),
      },
      ...options,
    ];
  }, [allExchanges, t, account, exchangeToDisplayName]);

  const gracePercentSelect = (
    <div key="gracePercent">
      {gracePercentTitle && (
        <FormFieldTitle>{gracePercentTitle}</FormFieldTitle>
      )}
      <Select
        style={{ width: "100%", maxWidth: "auto" }}
        value={gracePercentValue}
        onChange={gracePercentOnChange}
        popupMatchSelectWidth={false}
        optionLabelProp="label"
        placeholder={gracePercentPlaceholder}
        options={gracePercentOptions?.map((option) => ({
          value: option.value,
          label: option.name,
        }))}
        mobileTitle={gracePercentTitle}
      />
    </div>
  );

  const graceBaseline = graceBaselineTitle ? (
    <div key="graceBaseline">
      <FormFieldTitle>
        {graceBaselineTitle}
        <HelperTooltip
          analyticsFieldName={
            isEntry ? "Entry Grace Baseline" : "Take Profit Grace Baseline"
          }
          analyticsPageName="Bot Form"
          description={[
            t("helper_tooltips.entry_grace.line1"),
            t("helper_tooltips.entry_grace.line2"),
            t("helper_tooltips.entry_grace.line3"),
          ]}
        />
      </FormFieldTitle>
      <StyledRadioGroup
        value={graceBaselineValue}
        options={graceBaselineOptions!}
        onChange={graceBaselineOnChange!}
        size={20}
      />
    </div>
  ) : null;

  const targetExchangeSelect = allowGraceTargetExchange ? (
    <div key="graceTargetExchange">
      <FormFieldTitle>
        {t("labels.target_exchange")}
        <HelperTooltip
          analyticsFieldName={
            isEntry
              ? "Entry Grace Target Exchange"
              : "Take Profit Grace Target Exchange"
          }
          analyticsPageName="Bot Form"
          description={t(
            `helper_tooltips.form.${
              isEntry ? "entry" : "take_profit"
            }_grace_target_exchange.description`
          )}
        />
      </FormFieldTitle>
      <Select
        style={{ width: "100%", maxWidth: "auto" }}
        value={graceTargetExchangeValue}
        onChange={graceTargetExchangeOnChange}
        popupMatchSelectWidth={false}
        optionLabelProp="label"
        placeholder={graceTargetExchangePlaceholder}
        options={exchangeOptions}
        mobileTitle={t("labels.target_exchange")}
      />
    </div>
  ) : null;

  const items = [
    gracePercentSelect,
    graceBaseline,
    targetExchangeSelect,
  ].filter(Boolean);
  const numColumns = singleColumnLayout
    ? 1
    : twoColumnsLayout
    ? 2
    : Math.max(items.length, 2);

  return (
    <EqualColumnsGrid numColumns={numColumns} style={{ paddingLeft: 4 }}>
      {items}
    </EqualColumnsGrid>
  );
};

export default GracePercent;

const ACCOUNT_EXCHANGE_VALUE = "account_exchange";
